import {isBefore, startOfDay} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {openDialog, showNotification} from 'platform/components';
import {useDateTimeFormatter, useLocale} from 'platform/locale';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  useGetBusinessCaseQuery,
  useGetVehicleQuery,
  useLazyGetBusinessCaseQuery,
  useSendBusinessCaseOnlineOfferMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri, urlBuilder} from '@dms/routes';

import {Nullish, parseDate} from 'shared/web';

import {InvalidateOnlineOfferDialog} from '../components/InvalidateOnlineOfferDialog/InvalidateOnlineOfferDialog';
import {OfferValidityDialog} from '../components/OfferValidityDialog/OfferValidityDialog';
import {SendOnlineOfferEmailForm} from '../components/SendOnlineOfferEmailForm/SendOnlineOfferEmailForm';
import {usePermissions} from '../hooks/usePermissions/usePermissions';
import {createSearchParamsString} from '../utils/createSearchParamsString';
import {handleApiError} from '../utils/handleApiError';

const {workspace} = getWorkspaceFromUri();

export const useBcToOnlineOffer = (businessCaseId: string) => {
  const {language} = useLocale();
  const formatDateTime = useDateTimeFormatter();

  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId});
  const [getBusinessCase, {isLoading: isGetBusinessCaseLazyQueryLoading}] =
    useLazyGetBusinessCaseQuery();

  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: businessCaseData?.offers[0]?.saleVehicles?.[0]?.vehicleId ?? ''},
    {skip: isNilOrEmpty(businessCaseData?.offers[0]?.saleVehicles?.[0]?.vehicleId)}
  );

  const {data: participation} = useGetParticipationQuery({
    recordId: businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const {data: ownerInfo} = useGetUserQuery(
    {id: participation?.owner?.userId || ''},
    {skip: isNilOrEmpty(participation?.owner?.userId)}
  );

  const [createOnlineOffer, {isLoading: isCreateOnlineOfferMutationLoading}] =
    useSendBusinessCaseOnlineOfferMutation();

  const [canSendOnlineOffer, canInvalidateOnlineOffer, canReadOnlineOffer] = usePermissions({
    permissionKeys: ['sendOnlineOffer', 'invalidateOnlineOffer', 'readOnlineOffer'],
    scopes: {
      sendOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
      invalidateOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
      readOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const getOnlineOfferUri = (offerId?: string | Nullish, includeOpenedBy = true) =>
    urlBuilder({
      workspace,
      protocol: window.location.protocol,
      domain: process.env.ONLINE_OFFER_DOMAIN!,
      route: `/${offerId ?? businessCaseData?.offerId}`,
      searchParam: createSearchParamsString({
        lang: language,
        sellerEmail: ownerInfo?.email,
        openedBy: includeOpenedBy ? 'email' : undefined,
      }),
    });

  const isBusinessCaseInCorrectState =
    businessCaseData?.businessCaseState !== 'CONCEPT' &&
    businessCaseData?.businessCaseState !== 'UNSUCCESSFUL';

  const canSendOffer =
    isBusinessCaseInCorrectState &&
    canSendOnlineOffer &&
    isFeatureEnabled(featureFlags.BUSINESS_CASE_ONLINE_OFFER) &&
    isNilOrEmpty(businessCaseData?.onlineOfferFirstSentAt);

  const isOnlineOfferCreated =
    isNotNilOrEmpty(businessCaseData?.offerId) &&
    isFeatureEnabled(featureFlags.BUSINESS_CASE_ONLINE_OFFER);

  const isOnlineOfferSent =
    isNotNilOrEmpty(businessCaseData?.onlineOfferFirstSentAt) &&
    isFeatureEnabled(featureFlags.BUSINESS_CASE_ONLINE_OFFER);

  const canOpenOnlineOffer =
    isFeatureEnabled(featureFlags.BUSINESS_CASE_ONLINE_OFFER) &&
    canSendOnlineOffer &&
    isBusinessCaseInCorrectState;

  const canResendOffer = isBusinessCaseInCorrectState && canSendOnlineOffer && isOnlineOfferSent;

  const canInvalidateOffer = isOnlineOfferCreated && canInvalidateOnlineOffer && isOnlineOfferSent;

  const formattedOfferValidTo = businessCaseData?.offerValidTo
    ? formatDateTime('dateShort', parseDate(businessCaseData?.offerValidTo))
    : undefined;

  const isOfferExpired = businessCaseData?.offerValidTo
    ? isBefore(parseDate(businessCaseData.offerValidTo), startOfDay(new Date()))
    : false;

  const sendOnlineOfferToCustomer = ({
    shouldCreateOffer = false,
    onSuccess,
  }: {
    shouldCreateOffer?: boolean;
    onSuccess?: () => void;
  } = {}) => {
    // should not happen, just type guard check
    if (businessCaseData && participation?.owner?.userId && vehicleData && ownerInfo?.email) {
      const coverPhotoOfVehicle = vehicleData?.photos?.find((photo) => photo.isCoverPhoto);
      openDialog(
        <SendOnlineOfferEmailForm
          {...(shouldCreateOffer
            ? {
                sellerEmail: ownerInfo.email,
                shouldCreateOffer: true,
              }
            : {
                onlineOfferUri: getOnlineOfferUri(),
                shouldCreateOffer: false,
              })}
          businessCaseData={businessCaseData}
          sellerId={participation.owner.userId}
          vehicleId={vehicleData.id}
          photoId={coverPhotoOfVehicle?.fileId}
          onSuccess={onSuccess}
        />,
        {
          title: i18n.t('entity.sendEmailOnlineOfferDialog.heading'),
          id: 'sendEmailOnlineOfferDialog',
          'data-testid': 'sendEmailOnlineOfferDialog',
          scrollBehavior: 'inside',
          size: 'large',
          disableMaxHeight: true,
        }
      );
    }
  };

  const invalidateOnlineOfferToCustomer = () => {
    if (businessCaseData?.offerId) {
      openDialog(<InvalidateOnlineOfferDialog offerId={businessCaseData.offerId} />, {
        title: i18n.t('entity.invalidateOnlineOfferDialog.title'),
        id: 'invalidateOnlineOfferDialog',
        'data-testid': 'invalidateOnlineOfferDialog',
      });
    }
  };

  const openOffer = async () => {
    let offerId = businessCaseData?.offerId;

    if (!isOnlineOfferCreated) {
      await createOnlineOffer({businessCaseId}).unwrap().catch(handleApiError);
      const newBcData = await getBusinessCase({businessCaseId}).unwrap();
      if (!newBcData.offerId) {
        showNotification.error();
        return;
      }
      offerId = newBcData.offerId;
    }

    const onlineOfferUrl = getOnlineOfferUri(offerId, false);
    window.open(onlineOfferUrl, '_blank')?.focus();
  };

  const openOfferValidityDialog = () => {
    openDialog(
      <OfferValidityDialog
        currentOfferValidTo={businessCaseData?.offerValidTo}
        businessCaseId={businessCaseId}
      />,
      {
        title: i18n.t('entity.offerValidityDialog.title'),
        id: 'offerValidityDialog',
        'data-testid': 'offerValidityDialog',
        size: 'small',
      }
    );
  };

  return {
    canSendOffer,
    canResendOffer,
    canInvalidateOffer,
    isOnlineOfferCreated,
    isOnlineOfferSent,
    canOpenOnlineOffer,
    canReadOnlineOffer,
    offerValidity: {
      validTo: formattedOfferValidTo,
      isExpired: isOfferExpired,
      openDialog: openOfferValidityDialog,
    },
    isOpenOnlineOfferLoading:
      isGetBusinessCaseLazyQueryLoading || isCreateOnlineOfferMutationLoading,
    actions: {
      sendOnlineOfferToCustomer,
      invalidateOnlineOfferToCustomer,
      openOffer,
    },
  };
};
